import { useEffect, useState } from 'react'
import { useLogin } from '../hooks/useLogin'
import { useForgotPassword } from '../hooks/useForgotPassword'
import ForgotPasswordModal from './ForgotPasswordModal'
import ForgotEmailModal from './ForgotEmailModal'

interface LoginFormProps {
  onClearErrors: () => void
  onLoadingChange?: (loading: boolean) => void
}

function LoginForm({ onClearErrors, onLoadingChange }: LoginFormProps) {
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    isLoading,
    emailError,
    passwordError,
    setEmailError,
    setPasswordError,
    handleSubmit,
    togglePasswordVisibility,
  } = useLogin()

  const {
    showModal: showForgotModal,
    openModal: openForgotModal,
    closeModal: closeForgotModal,
    email: forgotEmail,
    setEmail: setForgotEmail,
    sendResetEmail,
    isLoading: isForgotLoading,
    success: forgotSuccess,
    error: forgotError,
  } = useForgotPassword()

  const [showForgotEmailModal, setShowForgotEmailModal] = useState(false)

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (emailError) setEmailError(null)
    onClearErrors()
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (passwordError) setPasswordError(null)
    onClearErrors()
  }

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={`form-group ${emailError ? 'has-error' : ''}`}>
          <label htmlFor="email">メールアドレス</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Email"
            autoComplete="email"
            className={emailError ? 'error' : ''}
          />
          {emailError && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{emailError}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${passwordError ? 'has-error' : ''}`}>
          <label htmlFor="password">パスワード</label>
          <div className="password-input-wrapper">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              placeholder="パスワード"
              autoComplete="current-password"
              className={passwordError ? 'error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
            >
              <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
            </button>
          </div>
          {passwordError && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{passwordError}</span>
            </div>
          )}
        </div>
        <button type="submit" className="login-button" disabled={isLoading}>
          {isLoading ? 'ログイン中...' : 'ログイン'}
        </button>
        <div className="form-links">
          <a
            href="#"
            className="link-text"
            onClick={(e) => {
              e.preventDefault()
              openForgotModal(email)
            }}
          >
            パスワードを忘れた場合
          </a>
          <a
            href="#"
            className="link-text"
            onClick={(e) => {
              e.preventDefault()
              setShowForgotEmailModal(true)
            }}
          >
            メールアドレスを忘れた場合
          </a>
        </div>
      </form>

      <ForgotPasswordModal
        show={showForgotModal}
        email={forgotEmail}
        onEmailChange={setForgotEmail}
        onSubmit={sendResetEmail}
        onClose={closeForgotModal}
        isLoading={isForgotLoading}
        success={forgotSuccess}
        error={forgotError}
      />

      <ForgotEmailModal
        show={showForgotEmailModal}
        onClose={() => setShowForgotEmailModal(false)}
      />
    </>
  )
}

export default LoginForm

